import {
  BookingProductType,
  BookingState,
  BookingTariff,
  BookingWorkshop,
  isBookingProductTypePackage,
  isBookingProductTypeTariff,
  RemoveBookingWorkshopAssignedHolderTariffs,
  RemoveWorkshopBookingsAndSetSeats,
  SetWorkshopBookingsAndSetSeats
} from '@products/models/booking.model';
import { AppConstants } from '@shared/app-constants';
import { findBookedPackageTariff, findBookedTariff } from '@store/products/booking/product/product-booking.reducer';
import { cloneDeep, difference, isEmpty } from 'lodash';

/**
 * Set workshop in booking
 * @description Set workshop in booking
 * @param state
 * @param props
 * @returns State
 */
export const setWorkshopInBooking = (state: BookingState, props: SetWorkshopBookingsAndSetSeats) => {
  const setWorkshopBookingsAndSetSeats: SetWorkshopBookingsAndSetSeats = cloneDeep(props);

  if (setWorkshopBookingsAndSetSeats) {
    const { setBookingWorkshops } = setWorkshopBookingsAndSetSeats;

    if (setBookingWorkshops.length) {
      const cloneStateProductsList: BookingProductType[] = cloneDeep(state.list);

      const setWorkshop = (setBookingWorkshops: BookingWorkshop[], bookedTariff: BookingTariff) => {
        setBookingWorkshops.forEach((setBookingWorkshop) => {
          const { id, tariffUuids } = setBookingWorkshop;
          const bookedTariffWorkshop = bookedTariff.workshops.find((bookedWorkshop) => bookedWorkshop.id === id);

          if (bookedTariffWorkshop) {
            tariffUuids.forEach((tariffUuid) => bookedTariffWorkshop.tariffUuids.push(tariffUuid));
          } else {
            bookedTariff.workshops.push(setBookingWorkshop);
          }
        });
      };

      setBookingWorkshops.forEach((setBookingWorkshop) => {
        const { productType, ticketTypeId, ticketPersonId, voucherCode, packageNumber, packageIndex, workshops } =
          setBookingWorkshop;

        cloneStateProductsList.forEach((bookedProductType) => {
          if (bookedProductType.productType === productType) {
            if (isBookingProductTypeTariff(bookedProductType)) {
              const bookedTariff = findBookedTariff(bookedProductType, ticketTypeId, ticketPersonId, voucherCode);

              if (bookedTariff) {
                setWorkshop(workshops, bookedTariff);
              }
            } else if (isBookingProductTypePackage(bookedProductType)) {
              if (
                bookedProductType.packageNumber === packageNumber &&
                bookedProductType.packageIndex === packageIndex
              ) {
                bookedProductType.productTypes.forEach((bookedPackageProductType) => {
                  if (isBookingProductTypeTariff(bookedPackageProductType)) {
                    const bookedPackageTariff = findBookedPackageTariff(
                      bookedPackageProductType,
                      ticketTypeId,
                      ticketPersonId,
                      voucherCode,
                      packageNumber,
                      packageIndex
                    );

                    if (bookedPackageTariff) {
                      setWorkshop(workshops, bookedPackageTariff);
                    }
                  }
                });
              }
            }
          }
        });
      });

      return {
        ...state,
        list: cloneStateProductsList
      };
    }
  }

  return state;
};

/**
 * Remove workshop from booking
 * @description Remove workshop from booking
 * @param state
 * @param props
 * @returns State
 */
export const removeWorkshopFromBooking = (state: BookingState, props: RemoveWorkshopBookingsAndSetSeats) => {
  const removeWorkshopBookingsAndSetSeats: RemoveWorkshopBookingsAndSetSeats = cloneDeep(props);

  if (removeWorkshopBookingsAndSetSeats) {
    const { removeBookingWorkshops } = removeWorkshopBookingsAndSetSeats;

    if (removeBookingWorkshops.length) {
      const cloneStateProductsList: BookingProductType[] = cloneDeep(state.list);

      const removeWorkshop = (
        removeBookingWorkshopsAssignedHolderTariffs: RemoveBookingWorkshopAssignedHolderTariffs[],
        bookedTariff: BookingTariff
      ) => {
        removeBookingWorkshopsAssignedHolderTariffs.forEach((removeBookingWorkshopAssignedHolders) => {
          const { workshopId, removeTariffUuids } = removeBookingWorkshopAssignedHolders;
          const bookedTariffWorkshopIndex = bookedTariff.workshops.findIndex(
            (bookedWorkshop) => bookedWorkshop.id === workshopId
          );

          if (bookedTariffWorkshopIndex !== AppConstants.INDEX_NOT_FOUND) {
            const bookedTariffWorkshop = bookedTariff.workshops[bookedTariffWorkshopIndex];

            bookedTariffWorkshop.tariffUuids = difference(bookedTariffWorkshop.tariffUuids, removeTariffUuids);

            if (isEmpty(bookedTariffWorkshop.tariffUuids)) {
              bookedTariff.workshops.splice(bookedTariffWorkshopIndex, 1);
            }
          }
        });
      };

      removeBookingWorkshops.forEach((removeBookingWorkshop) => {
        const {
          productType,
          ticketTypeId,
          ticketPersonId,
          voucherCode,
          packageNumber,
          packageIndex,
          removeWorkshopsAssignedHolderTariffs
        } = removeBookingWorkshop;

        cloneStateProductsList.forEach((bookedProductType) => {
          if (bookedProductType.productType === productType) {
            if (isBookingProductTypeTariff(bookedProductType)) {
              const bookedTariff = findBookedTariff(bookedProductType, ticketTypeId, ticketPersonId, voucherCode);

              if (bookedTariff) {
                removeWorkshop(removeWorkshopsAssignedHolderTariffs, bookedTariff);
              }
            } else if (isBookingProductTypePackage(bookedProductType)) {
              if (
                bookedProductType.packageNumber === packageNumber &&
                bookedProductType.packageIndex === packageIndex
              ) {
                bookedProductType.productTypes.forEach((bookedPackageProductType) => {
                  if (isBookingProductTypeTariff(bookedPackageProductType)) {
                    const bookedPackageTariff = findBookedPackageTariff(
                      bookedPackageProductType,
                      ticketTypeId,
                      ticketPersonId,
                      voucherCode,
                      packageNumber,
                      packageIndex
                    );

                    if (bookedPackageTariff) {
                      removeWorkshop(removeWorkshopsAssignedHolderTariffs, bookedPackageTariff);
                    }
                  }
                });
              }
            }
          }
        });
      });

      return {
        ...state,
        list: cloneStateProductsList
      };
    }
  }

  return state;
};
